import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import { useNavigate } from "react-router-dom";
import FormLabel from "../../components/FormLabel";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import api from "../../services/api";
import usePermission from "../../hooks/usePermission";
import { formatCurrencyFromDb, maskCep, maskCurrency, maskPhone } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";

const CadastrarImposto = ({ impostoId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(impostoId);
    const podeDeletar = usePermission("DELETAR_IMPOSTO");

    // Estado único do formulário
    const [formData, setFormData] = useState({
        id: "",
        tipoId: "",
        descricao: "",
        valor: "",
        dataVencimento: "",
        dataPagamento: "",
        statusId: "",
        referencia: "",
    });

    const [tipos, setTipos] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showAlertDialog, setShowAlertDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    useEffect(() => {
        fetchTipos();
        fetchStatus();
        if (isEditMode) fetchImposto();
    }, [isEditMode, impostoId]);

    const fetchImposto = async () => {
        try {
            const response = await api.get(`/impostos/${impostoId}`);
            const data = response.data;
            setFormData({
                id: data.id,
                tipoId: data.tipo?.id || "",
                descricao: data.descricao || "",
                valor: data.valor
                    ? formatCurrencyFromDb(data.valor)
                    : "",
                dataVencimento: data.dataVencimento ? data.dataVencimento.substring(0, 10) : "",
                dataPagamento: data.dataPagamento ? data.dataPagamento.substring(0, 10) : "",
                statusId: data.status?.id || "",
                referencia: data.referencia || "",
            });
        } catch (error) {
            setAlertMessage("Erro ao carregar o imposto. Tente novamente.");
            setShowAlertDialog(true);
        }
    };

    const fetchTipos = async () => {
        try {
            const resp = await api.get("/tiposimposto");
            setTipos(resp.data);
        } catch (err) { }
    };

    const fetchStatus = async () => {
        try {
            const resp = await api.get("/statuses/byModule/nome/impostos");
            setStatusList(resp.data);
        } catch (err) { }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirmDialog(true);
    };

    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/impostos/${impostoId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/impostos");
        } catch (error) {
            setAlertMessage("Erro ao remover imposto.");
            setShowAlertDialog(true);
        }
    };

    const handleConfirm = async () => {
        setShowConfirmDialog(false);
        try {
            const payload = {
                tipoId: formData.tipoId,
                descricao: formData.descricao,
                valor: parseCurrencyToFloat(formData.valor),
                dataVencimento: formData.dataVencimento,
                dataPagamento: formData.dataPagamento,
                statusId: formData.statusId,
                referencia: formData.referencia,
            };
            if (isEditMode) {
                await api.put(`/impostos/${impostoId}`, payload);
            } else {
                await api.post(`/impostos`, payload);
            }
            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                navigate(isEditMode ? `/impostos/editar/${impostoId}` : `/impostos`);
            }, 2000);
        } catch (error) {
            setAlertMessage(error.response?.data?.error || "Ocorreu um erro. Verifique os dados e tente novamente.");
            setShowAlertDialog(true);
        }
    };

    const handleCurrencyChange = (fieldName) => (e) => {
        const originalValue = e.target.value;
        const masked = maskCurrency(originalValue);
        setFormData((prev) => ({ ...prev, [fieldName]: masked }));
    };

    // Botão Voltar
    const handleBack = () => navigate("/impostos");

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Imposto" : "Cadastrar Imposto"}
                    </h1>
                </div>

                {/* INFORMAÇÕES */}
                <div className="flex flex-col gap-6">
                    <div className="text-stone-600 text-base font-medium">Informações Básicas</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* COLUNA 1 */}
                        <div className="flex flex-col gap-4">
                            {/* ID */}
                            <div className="w-full flex flex-col gap-1.5">
                                <label className="text-slate-700 text-sm font-medium leading-tight">ID</label>
                                <input
                                    type="text"
                                    className="pl-3 pr-14 py-2 bg-gray-100 rounded-md border border-slate-300"
                                    value={formData.id}
                                    readOnly
                                />
                            </div>

                            {/* Tipo */}
                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="tipoId" isRequired={true}>Tipo</FormLabel>
                                <select
                                    name="tipoId"
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                    value={formData.tipoId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Selecione</option>
                                    {tipos.map((t) => (
                                        <option key={t.id} value={t.id}>{t.nome}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Descrição */}
                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="descricao" isRequired={true}>Descrição</FormLabel>
                                <input
                                    name="descricao"
                                    type="text"
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                    value={formData.descricao}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            {/* Referência */}
                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="referencia">Referência</FormLabel>
                                <input
                                    name="referencia"
                                    type="text"
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                    value={formData.referencia}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        {/* COLUNA 2 */}
                        <div className="flex flex-col gap-4">
                            {/* Valor */}
                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="valor" isRequired={true}>Valor</FormLabel>
                                <input
                                    name="valor"
                                    type="text"
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                    value={formData.valor}
                                    onChange={handleCurrencyChange("valor")}
                                    required
                                />
                            </div>

                            {/* Data Vencimento */}
                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="dataVencimento" isRequired={true}>Data de Vencimento</FormLabel>
                                <input
                                    name="dataVencimento"
                                    type="date"
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                    value={formData.dataVencimento}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            {/* Data Pagamento */}
                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="dataPagamento">Data de Pagamento</FormLabel>
                                <input
                                    name="dataPagamento"
                                    type="date"
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                    value={formData.dataPagamento}
                                    onChange={handleInputChange}
                                />
                            </div>

                            {/* Status */}
                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="statusId" isRequired={true}>Status</FormLabel>
                                <select
                                    name="statusId"
                                    className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                    value={formData.statusId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Selecione</option>
                                    {statusList.map((s) => (
                                        <option key={s.id} value={s.id}>{s.nome}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* BOTÕES */}
                <div className="flex items-center w-full gap-4">
                    {isEditMode && podeDeletar && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={() => setShowConfirmDeleteRegister(true)}
                        >
                            <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Apagar" />
                            <span>Apagar</span>
                        </button>
                    )}
                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                        >
                            <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                            <span>Salvar</span>
                        </button>
                    </div>
                </div>
                {/* MODAIS */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirm}
                    onCancel={() => setShowConfirmDialog(false)}
                    message="Deseja salvar as informações?"
                />
                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="Informações salvas com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />
                <ConfirmationModal
                    isVisible={showConfirmDeleteRegister}
                    onConfirm={handleDeleteRegister}
                    onCancel={() => setShowConfirmDeleteRegister(false)}
                    message="Deseja apagar este registro?"
                    textButtonConfirm="Sim"
                />
                {showAlertDialog && (
                    <AlertDialog
                        isVisible={showAlertDialog}
                        message={alertMessage}
                        onClose={() => setShowAlertDialog(false)}
                    />
                )}
            </form>
        </Template>
    );
};

export default CadastrarImposto;
